import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAdminAuth } from "../../../hooks/useAdminAuth";
import { useAdminReview } from "../../../hooks/useAdminReview";
import { ReviewDetailResponse } from "../../../types/review";
import { Button, Card } from "../../ui";
import Icon from "../../ui/Icon";

const ReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const { messageId } = useParams<{ messageId: string }>();
  const [searchParams] = useSearchParams();
  const [reviewDetail, setReviewDetail] = useState<ReviewDetailResponse | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedAction, setSelectedAction] = useState<
    "APPROVED" | "REJECTED" | null
  >(null);
  const [reviewReason, setReviewReason] = useState("");

  const { getReviewDetail, updateReview } = useAdminReview();
  const { adminLogout } = useAdminAuth();

  // 검수 상세 정보 조회
  useEffect(() => {
    const fetchDetail = async () => {
      if (!messageId) {
        toast.error("잘못된 요청입니다.");
        navigate("/admin/dashboard");
        return;
      }

      setIsLoading(true);
      try {
        const detail = await getReviewDetail(messageId);
        if (detail) {
          setReviewDetail(detail);
        } else {
          toast.error("검수 정보를 찾을 수 없습니다.");
          const tab = searchParams.get("tab");
          const dashboardUrl = tab
            ? `/admin/dashboard?tab=${tab}`
            : "/admin/dashboard";
          navigate(dashboardUrl);
        }
    } catch {
      toast.error("검수 정보 로딩 중 오류가 발생했습니다.");
        const tab = searchParams.get("tab");
        const dashboardUrl = tab
          ? `/admin/dashboard?tab=${tab}`
          : "/admin/dashboard";
        navigate(dashboardUrl);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetail();
  }, [messageId, getReviewDetail, navigate, searchParams]);

  // 뒤로 가기
  const handleGoBack = () => {
    const tab = searchParams.get("tab");
    const dashboardUrl = tab
      ? `/admin/dashboard?tab=${tab}`
      : "/admin/dashboard";
    navigate(dashboardUrl);
  };

  // 로그아웃
  const handleLogout = () => {
    if (confirm("관리자 로그아웃 하시겠습니까?")) {
      adminLogout();
    }
  };

  // 저장 처리
  const handleSave = async () => {
    if (!messageId || !selectedAction) {
      toast.error("승인 또는 거부를 선택해 주세요.");
      return;
    }

    setIsProcessing(true);
    try {
      await updateReview(messageId, selectedAction, reviewReason);

      const message =
        selectedAction === "APPROVED"
          ? "AI 답변이 승인되었습니다."
          : "AI 답변이 거부되었습니다.";
      toast.success(message);

      const tab = searchParams.get("tab");
      const dashboardUrl = tab
        ? `/admin/dashboard?tab=${tab}`
        : "/admin/dashboard";
      navigate(dashboardUrl);
    } catch {
      toast.error("저장 중 오류가 발생했습니다.");
    } finally {
      setIsProcessing(false);
    }
  };

  // 시간 포맷팅
  const formatDateTime = (date: Date | string | null | undefined) => {
    if (!date) return "-";

    try {
      const d = typeof date === "string" ? new Date(date) : date;

      if (isNaN(d.getTime())) return "-";

      return d.toLocaleString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "-";
    }
  };

  if (isLoading) {
    return (
      <div className="h-screen-dvh bg-gray-50 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="text-gray-600">로딩 중...</span>
        </div>
      </div>
    );
  }

  if (!reviewDetail) {
    return (
      <div className="h-screen-dvh bg-gray-50 flex flex-col">
        {/* 헤더 */}
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shadow-sm">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
              <Icon name="shield" className="text-white" size="sm" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-800">
                AI 답변 검수
              </h1>
              <p className="text-xs text-gray-500">관리자 검수 시스템</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            title="로그아웃"
          >
            <Icon name="logOut" size="sm" />
            <span className="text-sm font-medium">로그아웃</span>
          </button>
        </header>

        <div className="flex-1 flex items-center justify-center">
          <div className="text-center py-12">
            <Icon
              name="alertTriangle"
              className="mx-auto mb-4 text-gray-400"
              size="xl"
            />
            <h3 className="text-lg font-medium text-gray-800 mb-2">
              검수 정보를 찾을 수 없습니다
            </h3>
            <Button onClick={handleGoBack} variant="outline">
              목록으로 돌아가기
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen-dvh bg-gray-50 flex flex-col">
      {/* 헤더 */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shadow-sm">
        <div className="flex items-center">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
            <Icon name="shield" className="text-white" size="sm" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-800">
              AI 답변 검수
            </h1>
            <p className="text-xs text-gray-500">관리자 검수 시스템</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          title="로그아웃"
        >
          <Icon name="logOut" size="sm" />
          <span className="text-sm font-medium">로그아웃</span>
        </button>
      </header>

      {/* 메인 콘텐츠 - 스크롤 가능 */}
      <main className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* 뒤로가기 버튼 */}
          <div className="flex items-center justify-between">
            <button
              onClick={handleGoBack}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
            >
              <Icon name="chevronLeft" size="sm" />
              <span>목록으로 돌아가기</span>
            </button>
          </div>

          {/* 질문과 답변 */}
          <div className="space-y-4">
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                사용자 질문
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-900 whitespace-pre-wrap leading-relaxed">
                  {reviewDetail.userQuestion}
                </p>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-semibold text-blue-700 mb-4">
                AI 답변
              </h3>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-gray-900 whitespace-pre-wrap leading-relaxed">
                  {reviewDetail.aiResponse}
                </p>
              </div>
            </Card>
          </div>
        </div>
      </main>

      {/* 하단 고정 검수 영역 */}
      <div className="bg-white border-t border-gray-200 px-4 py-4">
        <div className="max-w-4xl mx-auto">
          {reviewDetail.reviewStatus === "PENDING" ? (
            // 검수 대기 중인 경우 - 편집 가능
            <>
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                <h3 className="text-lg font-semibold text-gray-900">
                  검수 처리
                </h3>
                <span className="text-sm text-yellow-600 bg-yellow-50 px-2 py-1 rounded-full">
                  검수 대기 중
                </span>
              </div>

              <div className="space-y-4">
                {/* 검수 결과 선택 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    검수 결과
                  </label>
                  <div className="flex space-x-6">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="reviewAction"
                        value="APPROVED"
                        checked={selectedAction === "APPROVED"}
                        onChange={(e) =>
                          setSelectedAction(e.target.value as "APPROVED")
                        }
                        className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                      />
                      <span className="ml-2 text-sm text-gray-900">승인</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="reviewAction"
                        value="REJECTED"
                        checked={selectedAction === "REJECTED"}
                        onChange={(e) =>
                          setSelectedAction(e.target.value as "REJECTED")
                        }
                        className="w-4 h-4 text-red-600 border-gray-300 focus:ring-red-500"
                      />
                      <span className="ml-2 text-sm text-gray-900">거부</span>
                    </label>
                  </div>
                </div>

                {/* 검수 사유 입력 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    검수 사유 (선택사항)
                  </label>
                  <textarea
                    value={reviewReason}
                    onChange={(e) => setReviewReason(e.target.value)}
                    placeholder={
                      selectedAction === "APPROVED"
                        ? "예: 정확하고 유용한 답변입니다, 의학적으로 올바른 정보입니다 등"
                        : selectedAction === "REJECTED"
                        ? "예: 부정확한 정보가 포함되어 있음, 질문과 관련성이 낮음 등"
                        : "검수 사유를 입력해 주세요"
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    rows={3}
                  />
                </div>

                {/* 저장 버튼 */}
                <Button
                  onClick={handleSave}
                  disabled={isProcessing || !selectedAction}
                  className="bg-blue-600 hover:bg-blue-700 w-full"
                >
                  <Icon name="save" className="mr-2" size="sm" />
                  {isProcessing ? "저장 중..." : "저장"}
                </Button>
              </div>
            </>
          ) : (
            // 검수 완료된 경우 - 읽기 전용
            <>
              <div className="flex items-center space-x-3 mb-3">
                <div
                  className={`w-2 h-2 rounded-full ${
                    reviewDetail.reviewStatus === "APPROVED"
                      ? "bg-green-500"
                      : "bg-red-500"
                  }`}
                ></div>
                <h3 className="text-lg font-semibold text-gray-900">
                  검수 완료
                </h3>
                <span
                  className={`text-sm px-2 py-1 rounded-full ${
                    reviewDetail.reviewStatus === "APPROVED"
                      ? "text-green-600 bg-green-50"
                      : "text-red-600 bg-red-50"
                  }`}
                >
                  {reviewDetail.reviewStatus === "APPROVED"
                    ? "승인됨"
                    : "거부됨"}
                </span>
              </div>

              <div className="space-y-4">
                {/* 검수 결과 표시 (읽기 전용) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    검수 결과
                  </label>
                  <div className="flex space-x-6">
                    <div className="flex items-center">
                      <div
                        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          reviewDetail.reviewStatus === "APPROVED"
                            ? "border-green-500 bg-green-500"
                            : "border-gray-300"
                        }`}
                      >
                        {reviewDetail.reviewStatus === "APPROVED" && (
                          <Icon name="check" className="text-white" size="xs" />
                        )}
                      </div>
                      <span
                        className={`ml-2 text-sm ${
                          reviewDetail.reviewStatus === "APPROVED"
                            ? "text-green-700 font-medium"
                            : "text-gray-500"
                        }`}
                      >
                        승인
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div
                        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          reviewDetail.reviewStatus === "REJECTED"
                            ? "border-red-500 bg-red-500"
                            : "border-gray-300"
                        }`}
                      >
                        {reviewDetail.reviewStatus === "REJECTED" && (
                          <Icon name="x" className="text-white" size="xs" />
                        )}
                      </div>
                      <span
                        className={`ml-2 text-sm ${
                          reviewDetail.reviewStatus === "REJECTED"
                            ? "text-red-700 font-medium"
                            : "text-gray-500"
                        }`}
                      >
                        거부
                      </span>
                    </div>
                  </div>
                </div>

                {/* 검수 사유 표시 (읽기 전용) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    검수 사유
                  </label>
                  <div className="bg-gray-50 p-3 border border-gray-200 rounded-lg">
                    <p className="text-gray-900 text-sm whitespace-pre-wrap">
                      {reviewDetail.reviewReason ||
                        "사유가 입력되지 않았습니다."}
                    </p>
                  </div>
                </div>

                {/* 검수자 정보 */}
                <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-200">
                  <div>
                    <span className="text-sm font-medium text-gray-700">
                      검수자
                    </span>
                    <p className="text-gray-900 text-sm mt-1">
                      {reviewDetail.reviewedByAdminName || "알 수 없음"}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700">
                      검수 완료 시간
                    </span>
                    <p className="text-gray-900 text-sm mt-1">
                      {formatDateTime(reviewDetail.reviewedAt)}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewPage;
